// 수학 출제하기의 '범위 골라 출제' 탭. 과목 → 단원 → 소분류 → 세부유형을 고르고 조건으로 뽑는다.
// 분류표는 수학 사이트 /bank/units/ (서버 api/assignment/math_bank.py). 문항 수는 어디에도 보이지 않는다(2026-11-03 대표).
import React, { useEffect, useMemo, useState } from "react";

import apiClient from "../../../services/apiClient";
import { MathBankChipList, MathBankField, MathBankFilterFields, toggleInList } from "./MathBankControls";
import { MAX_TOTAL, problemFilterPayloadOf } from "./mathBankShared";

const MathBankRangePanel = ({ filter, onFilterChange, hasPickedProblems, onPicked }) => {
  const [unitData, setUnitData] = useState(null);
  const [subjectCode, setSubjectCode] = useState("");
  const [openCodeList, setOpenCodeList] = useState([]);
  const [unitCodeList, setUnitCodeList] = useState([]);
  const [skillCodeList, setSkillCodeList] = useState([]);
  const [typeCodeList, setTypeCodeList] = useState([]);
  const [difficultyList, setDifficultyList] = useState([]);
  const [kindList, setKindList] = useState([]);
  const [total, setTotal] = useState(20);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    apiClient
      .get("/api/v1/assignment/math/bank/units", { withAuth: true })
      .then((response) => {
        setUnitData(response.data);
        setSubjectCode(response.data.subjects?.[0]?.code || "");
      })
      .catch((error) => setUnitData({ subjects: [], error: error?.response?.data?.error || "분류표를 불러오지 못했습니다." }));
  }, []);

  const subjectList = useMemo(() => unitData?.subjects || [], [unitData]);
  const subject = subjectList.find((one) => one.code === subjectCode);
  const levelList = useMemo(() => [...new Set(subjectList.map((one) => one.level))], [subjectList]);

  // 고른 범위를 이름으로 보여 주려고 모든 과목의 코드 → (이름, 과목 이름)을 한 번 만들어 둔다
  const rangeNameByCode = useMemo(() => {
    const nameByCode = {};
    subjectList.forEach((one) =>
      (one.units || []).forEach((unit) => {
        nameByCode[unit.code] = { name: unit.name, subjectName: one.name };
        (unit.skills || []).forEach((skill) => {
          nameByCode[skill.code] = { name: skill.name, subjectName: one.name };
          (skill.types || []).forEach((type) => (nameByCode[type.code] = { name: type.name, subjectName: one.name }));
        });
      })
    );
    return nameByCode;
  }, [subjectList]);

  // 단원을 고르면 그 아래 고른 것은 지운다(겹쳐 보내지 않는다). 아래를 고르면 그 단원은 푼다
  const toggleUnit = (unit) => {
    const childSkillList = (unit.skills || []).map((skill) => skill.code);
    const childTypeList = (unit.skills || []).flatMap((skill) => (skill.types || []).map((type) => type.code));
    setSkillCodeList((list) => list.filter((code) => !childSkillList.includes(code)));
    setTypeCodeList((list) => list.filter((code) => !childTypeList.includes(code)));
    setUnitCodeList((list) => toggleInList(list, unit.code));
  };
  const toggleSkill = (unit, skill) => {
    const childTypeList = (skill.types || []).map((type) => type.code);
    setUnitCodeList((list) => list.filter((code) => code !== unit.code));
    setTypeCodeList((list) => list.filter((code) => !childTypeList.includes(code)));
    setSkillCodeList((list) => toggleInList(list, skill.code));
  };
  const toggleType = (unit, skill, type) => {
    setUnitCodeList((list) => list.filter((code) => code !== unit.code));
    setSkillCodeList((list) => list.filter((code) => code !== skill.code));
    setTypeCodeList((list) => toggleInList(list, type.code));
  };
  const removeRange = (code) => {
    setUnitCodeList((list) => list.filter((one) => one !== code));
    setSkillCodeList((list) => list.filter((one) => one !== code));
    setTypeCodeList((list) => list.filter((one) => one !== code));
  };
  const clearRange = () => {
    setUnitCodeList([]);
    setSkillCodeList([]);
    setTypeCodeList([]);
  };
  const toggleOpen = (code) => setOpenCodeList((list) => toggleInList(list, code));

  const selectedCodeList = [...unitCodeList, ...skillCodeList, ...typeCodeList];

  // 과제 제목 기본값: 처음 고른 범위의 과목 이름 + 범위 이름
  const firstRangeTitle = () => {
    const first = rangeNameByCode[selectedCodeList[0]];
    return first ? `${first.subjectName} ${first.name}` : "";
  };

  const pick = async () => {
    if (!selectedCodeList.length) return alert("단원을 골라주세요.");
    if (isBusy) return;
    setIsBusy(true);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/bank/pick",
        {
          units: unitCodeList,
          skills: skillCodeList,
          types: typeCodeList,
          total,
          difficulties: difficultyList,
          kinds: kindList,
          ...problemFilterPayloadOf(filter),
        },
        { withAuth: true }
      );
      if (!response.data.problem_list.length) return alert("조건에 맞는 문제가 없습니다.");
      onPicked(response.data.problem_list, firstRangeTitle());
    } catch (error) {
      alert(error?.response?.data?.error || "문제를 뽑지 못했습니다.");
    } finally {
      setIsBusy(false);
    }
  };

  const renderCheckRow = ({ code, name, isChecked, onToggle, childCount, depth }) => (
    <div className={`math-bank-page__tree-row math-bank-page__tree-row--depth-${depth}`}>
      {childCount > 0 ? (
        <button
          type="button"
          className={`math-bank-page__chevron${openCodeList.includes(code) ? " math-bank-page__chevron--open" : ""}`}
          aria-label={openCodeList.includes(code) ? "접기" : "펼치기"}
          aria-expanded={openCodeList.includes(code)}
          onClick={() => toggleOpen(code)}
        />
      ) : (
        <span className="math-bank-page__chevron-space" />
      )}
      <label className="math-bank-page__check">
        <input type="checkbox" checked={isChecked} onChange={onToggle} />
        <span>{name}</span>
      </label>
    </div>
  );

  return (
    <div className="math-bank-page__layout math-bank-page__layout--range">
      <section className="math-bank-page__card">
        <div className="math-bank-page__card-head">
          <h4>범위</h4>
        </div>
        {!unitData && <p className="math-bank-page__empty">불러오는 중입니다.</p>}
        {unitData?.error && <p className="math-bank-page__empty">{unitData.error}</p>}
        {levelList.length > 0 && (
          <div className="math-bank-page__subject-table">
            {levelList.map((level) => (
              <div key={level} className="math-bank-page__subject-row">
                <div className="math-bank-page__subject-level">{level}</div>
                <MathBankChipList
                  optionList={subjectList.filter((one) => one.level === level).map((one) => ({ value: one.code, label: one.name }))}
                  isActive={(value) => value === subjectCode}
                  onPick={setSubjectCode}
                />
              </div>
            ))}
          </div>
        )}
        {subject && (
          <div className="math-bank-page__tree">
            {(subject.units || []).map((unit) => (
              <div key={unit.code} className="math-bank-page__tree-group">
                {renderCheckRow({
                  code: unit.code,
                  name: unit.name,
                  isChecked: unitCodeList.includes(unit.code),
                  onToggle: () => toggleUnit(unit),
                  childCount: (unit.skills || []).length,
                  depth: 0,
                })}
                {openCodeList.includes(unit.code) &&
                  (unit.skills || []).map((skill) => (
                    <React.Fragment key={skill.code}>
                      {renderCheckRow({
                        code: skill.code,
                        name: skill.name,
                        isChecked: skillCodeList.includes(skill.code),
                        onToggle: () => toggleSkill(unit, skill),
                        childCount: (skill.types || []).length,
                        depth: 1,
                      })}
                      {openCodeList.includes(skill.code) &&
                        (skill.types || []).map((type) => (
                          <React.Fragment key={type.code}>
                            {renderCheckRow({
                              code: type.code,
                              name: type.name,
                              isChecked: typeCodeList.includes(type.code),
                              onToggle: () => toggleType(unit, skill, type),
                              childCount: 0,
                              depth: 2,
                            })}
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  ))}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="math-bank-page__card math-bank-page__card--sticky">
        <div className="math-bank-page__card-head">
          <h4>조건</h4>
        </div>
        <MathBankField label={`고른 범위 ${selectedCodeList.length}`}>
          {selectedCodeList.length > 0 ? (
            <div className="math-bank-page__selected-list">
              {selectedCodeList.map((code) => (
                <span key={code} className="math-bank-page__selected">
                  <span className="math-bank-page__selected-name">{rangeNameByCode[code]?.name || code}</span>
                  <button
                    type="button"
                    className="math-bank-page__selected-remove"
                    aria-label="빼기"
                    onClick={() => removeRange(code)}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button type="button" className="math-bank-page__text-button" onClick={clearRange}>
                모두 풀기
              </button>
            </div>
          ) : (
            <p className="math-bank-page__placeholder">없음</p>
          )}
        </MathBankField>
        <MathBankFilterFields filter={filter} onChange={onFilterChange} />
        <MathBankField label="난이도">
          <MathBankChipList
            optionList={(unitData?.difficulties || []).map((one) => ({ value: one, label: one }))}
            isActive={(value) => difficultyList.includes(value)}
            onPick={(value) => setDifficultyList((list) => toggleInList(list, value))}
          />
        </MathBankField>
        <MathBankField label="형식">
          <MathBankChipList
            optionList={(unitData?.kinds || []).map((one) => ({ value: one.code, label: one.name }))}
            isActive={(value) => kindList.includes(value)}
            onPick={(value) => setKindList((list) => toggleInList(list, value))}
          />
        </MathBankField>
        <MathBankField label="문항 수">
          <input
            type="number"
            className="math-bank-page__number-input"
            min={1}
            max={MAX_TOTAL}
            value={total}
            onChange={(event) => setTotal(Math.max(1, Math.min(MAX_TOTAL, Number(event.target.value) || 1)))}
          />
        </MathBankField>
        <button type="button" className="math-bank-page__primary math-bank-page__primary--wide" onClick={pick} disabled={isBusy}>
          {isBusy ? "뽑는 중" : hasPickedProblems ? "다시 뽑기" : "문제 뽑기"}
        </button>
      </section>
    </div>
  );
};

export default MathBankRangePanel;
